import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import { AdminCoversSection } from "./components/AdminCoversSection";
import { AdminDocumentsSection } from "./components/AdminDocumentsSection";
import { AdminHijackUserButton } from "kw/admin/components/AdminHijackUserButton";
import { AdminMarkPaidQuoteButton } from "./components/AdminQuoteMarkPaidModal";
import { AdminPaymentsSection } from "./components/AdminPaymentsSection";
import { AdminPolicyPeriodsSection } from "./components/AdminPolicyPeriodsSection";
import { AdminPolicySummarySection } from "./components/AdminPolicySummarySection";
import { AdminPremiumBreakdownSection } from "./components/AdminPremiumBreakdownSection";
import { AdminRiskStateSection } from "./components/AdminRiskStateSection";
import { HeadingActionsBar } from "kanopi-components/HeadingActionBar/HeadingActionsBar";
import { Placeholder } from "kanopi-components/Placeholder";
import { SIZE } from "baseui/button";
import { SummaryCard } from "./components/SummaryCard";
import { Tab, Tabs } from "baseui/tabs-motion";
import { useGlobal } from "kanopi-components/useGlobal";
import { usePolicies } from "./hooks/usePolicies";
import { VerticalSpacer } from "kanopi-components/SpaceWrapper/VerticalSpacer";
import { AdminExposureItemsSection } from "./components/AdminExposureItemsSection";
import { AdminExposuresPolicySection } from "kw/admin/components/AdminExposuresPolicySection";
import { useQuotes } from "kw/admin/hooks/useQuotes";
import { useUser } from "kw/auth/useUser";
import { AdminRenewalsSection } from "./components/AdminRenewalsSection";
import { AdminDismissRenewalButton } from "./components/AdminDismissRenewalButton";
import { AdminPendingCancellationSection } from "./components/AdminPendingCancellationSection";
import { policyStatusBadgeColor } from "kw/util/appConstants";

export const PolicyDetailsPage = ({ policyId }: { policyId: string }) => {
  const { policy, loading, fetchPolicy } = usePolicies();
  const { quote, fetchQuote } = useQuotes();
  const user = useUser();
  const [, setHijack] = useGlobal("hijack");
  const [activeKey, setActiveKey] = React.useState("0");
  const markPaidFF = policy?.quote?.product?.key === "demo_commercial_combined";

  useEffect(() => {
    if (policyId) {
      fetchPolicy(policyId);
      setHijack({
        nextUrl: "/policies",
        releaseToUrl: `/admin/policies/${policyId}`,
      });
    }
  }, [policyId]);

  useEffect(() => {
    const quoteId = policy?.quote?.id;
    if (quoteId) {
      fetchQuote(quoteId);
    }
  }, [policy]);

  const hasEnabledExposures = !!quote?.riskProfile?.enabledExposures?.length;

  return (
    <>
      <Helmet>
        <title>{`Policy #${policy?.policyNumber} - ${user?.organization?.name}`}</title>
      </Helmet>
      <Placeholder loading={!policy || loading}>
        <VerticalSpacer>
          <HeadingActionsBar
            heading={`Policy #${policy?.policyNumber}`}
            caption={policy?.statusDisplay}
            captionColor={policyStatusBadgeColor[policy?.status]}
            actions={[
              ...(markPaidFF
                ? [
                    <AdminMarkPaidQuoteButton
                      key={1}
                      quote={policy?.quote}
                      size={SIZE.compact}
                      title="Mark as paid"
                      modalButtonLabel="Yes, mark this quote as paid"
                      data-testid={"acceptButton"}
                    />,
                  ]
                : []),
              <AdminHijackUserButton
                key={0}
                userId={policy?.riskProfile?.owner?.id}
                userIsActive={policy?.riskProfile?.owner?.isActive}
              />,
            ]}
          />
          <SummaryCard showHeader={false}>
            <Tabs
              activeKey={activeKey}
              onChange={({ activeKey }) => {
                setActiveKey(activeKey);
              }}
              activateOnFocus
            >
              <Tab title="Summary" data-testid="policySummarySection">
                <AdminPolicySummarySection policy={policy} />
              </Tab>
              <Tab
                title="Premium breakdown"
                data-testid="policyPremiumBreakdownSection"
              >
                <AdminPremiumBreakdownSection quote={policy?.quote} />
              </Tab>
              <Tab title="Current documents" data-testid="policyDocumentsSection">
                <AdminDocumentsSection documents={policy?.documents} />
              </Tab>
              <Tab title="Quotes" data-testid="policyQuotesSection">
                <AdminPolicyPeriodsSection periods={policy?.policyPeriods} />
              </Tab>
            </Tabs>
          </SummaryCard>
          <SummaryCard data-testid="adminCoversSection" title="Covers">
            <AdminCoversSection
              covers={policy?.quote?.covers?.map((cover) => cover?.key)}
              availableCovers={policy?.quote?.product?.availableCovers}
            />
          </SummaryCard>
          <SummaryCard data-testid="adminRiskStateSection" title="Policy details">
            <AdminRiskStateSection
              riskProfile={policy?.riskProfile}
              questionSets={policy?.riskProfile?.riskStateQuestionSets}
              hasLongLabels={true}
            />
          </SummaryCard>
          {/* TODO: DEV-4772 remove else part once products migrated to have exposures */}
          {hasEnabledExposures ? (
            <>
              <SummaryCard data-testid="adminExposuresPolicySection" title="Policy">
                <AdminExposuresPolicySection
                  enabledExposures={quote?.riskProfile?.enabledExposures.filter((e) => {
                    return e.type === "policy";
                  })}
                  riskProfile={quote?.riskProfile}
                  availableCovers={quote?.product?.availableCovers}
                  quote={quote}
                />
              </SummaryCard>
              <SummaryCard data-testid="adminExposuresSection" title="Exposures">
                <AdminExposureItemsSection
                  enabledExposures={quote?.riskProfile?.enabledExposures.filter((e) => {
                    return e.type !== "policy";
                  })}
                  availableCovers={quote?.product?.availableCovers}
                  quote={quote}
                  riskProfile={quote?.riskProfile}
                />
              </SummaryCard>
            </>
          ) : (
            <SummaryCard data-testid="adminCoversSection" title="Covers">
              <AdminCoversSection
                covers={quote?.covers?.map((cover) => cover?.key)}
                availableCovers={quote?.product?.availableCovers}
              />
            </SummaryCard>
          )}
          {policy?.latestCancellation && (
            <SummaryCard
              data-testid="adminPendingCancellationSection"
              title="This policy is pending cancellation"
            >
              <AdminPendingCancellationSection
                cancellation={policy?.latestCancellation}
              />
            </SummaryCard>
          )}
          {policy?.latestRenewal?.quote?.isBound === false && (
            <SummaryCard
              data-testid="adminRenewalsSection"
              title="Upcoming Renewal"
              ctas={[
                <AdminDismissRenewalButton
                  key={0}
                  renewalQuoteId={policy?.latestRenewal?.id}
                  fetchPolicy={fetchPolicy}
                />,
              ]}
            >
              <AdminRenewalsSection renewalQuote={policy?.latestRenewal?.quote} />
            </SummaryCard>
          )}
          <SummaryCard data-testid="adminPaymentsSection" title="Payments">
            <AdminPaymentsSection payments={policy?.transactions} />
          </SummaryCard>
        </VerticalSpacer>
      </Placeholder>
    </>
  );
};
